/**
 * Canonical Brand Kit editor.
 *
 * Hard rule: this is the only tenant visual-identity authoring surface.
 * Controls are active only when their value is persisted by the Branding API.
 * Everything else is explicitly disabled and excluded from Publish.
 */
import { useState, type ReactNode } from "react";
import {
  Banner, Btn, ColorPicker, DevicePreviewToggle, Dropdown,
  EditorSectionNav, Field, FileUpload, Icon, Input, Section, T, ToggleRow,
  resolveDeviceWidths, type PreviewDevice,
} from "@v0y4ge/jrni-ui";
import { Screen } from "../../shell/chrome";
import { isAdmin, useTenant } from "../../shell/access";
import { AuditTrailDrawer } from "../../shell/AuditTrail";
import { DataSourceStrip } from "../../shell/opSource";
import { useT } from "../../lib/i18n";
import { useDesignerLayout } from "../journeys/designer-layout";
import { GOOGLE_FONTS, LiveRegion, type BrandKit } from "./branding-kit";
import { BrandPreview, PREVIEW_JOURNEYS, type PreviewJourney } from "./branding-preview";

type PaneId = "overview" | "logo" | "colour" | "type" | "communications" | "wallets" | "roadmap";

const COMING_SOON = [
  "Import from website",
  "Reverse and icon logos",
  "Favicon, Apple touch and PWA icons",
  "Social / Open Graph image",
  "Email header logo",
  "Custom font upload",
  "Secondary and link colours",
  "Custom header, footer and CSS",
  "Sender email, reply-to and SMS identity",
  "Mailing address and unsubscribe content",
  "Privacy, terms, website and social profiles",
];

export default function BrandingDesigner({ kit }: { kit: BrandKit }) {
  const t = useT();
  const tenant = useTenant();
  const layout = useDesignerLayout();
  const {
    brand, set, assets, setAsset, legalNameSupported, canWrite, dirty,
    draftCount, save, discard, resetDefaults, readFailed, uploadImage,
    announcement,
  } = kit;
  const [pane, setPane] = useState<PaneId>("overview");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [device, setDevice] = useState<PreviewDevice>("desktop");
  const [journey, setJourney] = useState<PreviewJourney>("appointment");
  const [auditOpen, setAuditOpen] = useState(false);
  const editable = canWrite && !readFailed;

  const items = [
    { id: "overview", label: t("settings.brandKit.overview", "Overview") },
    { id: "logo", label: t("settings.branding.logo", "Logo") },
    { id: "colour", label: t("settings.brandKit.navColour", "Colour") },
    { id: "type", label: t("settings.branding.typography", "Typography") },
    { id: "communications", label: t("settings.branding.communications", "Communications") },
    { id: "wallets", label: t("settings.brandKit.wallets", "Wallet passes") },
    { id: "roadmap", label: t("settings.brandKit.comingSoon", "Coming soon"), count: COMING_SOON.length },
  ];

  const overview = (
    <>
      <Banner tone="primary" title={t("settings.brandKit.singleAuthority", "One brand, everywhere")}>
        {t("settings.brandKit.singleAuthorityBody", "Publish here once. Journeys, landing pages, hubs, queues, confirmations, emails and kiosks consume this canonical Brand Kit and cannot create a separate theme.")}
      </Banner>
      <Section title={t("settings.brandKit.publishedCoverage", "Published coverage")} description={t("settings.brandKit.publishedCoverageBody", "Only service-backed fields are editable. Design-system status colours, neutrals and component geometry remain platform-owned.")}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: T.space[3] }}>
          {[
            ["3", t("settings.brandKit.brandColoursCount", "brand colours")],
            ["2", t("settings.brandKit.fontRolesCount", "font roles")],
            ["1", t("settings.brandKit.logoAssetCount", "primary logo")],
            ["3", t("settings.brandKit.identityFieldsCount", "identity fields")],
          ].map(([value, label]) => (
            <div key={label} style={{ padding: T.space[4], border: `1px solid ${T.border}`, borderRadius: T.radius.md, background: T.white }}>
              <div style={{ fontSize: 22, fontWeight: 700, color: T.n1 }}>{value}</div>
              <div style={{ fontSize: 12.5, color: T.soft }}>{label}</div>
            </div>
          ))}
        </div>
      </Section>
    </>
  );

  const logo = (
    <>
      <Section title={t("settings.branding.primaryLogo", "Primary logo")} description={t("settings.brandKit.logoStoredBody", "Upload the logo used by every customer-facing surface. PNG, JPEG, WebP or SVG.")}>
        {assets.logo ? (
          <div style={{ minHeight: 120, padding: T.space[5], display: "flex", alignItems: "center", justifyContent: "center", border: `1px solid ${T.border}`, borderRadius: T.radius.md, background: T.n6 }}>
            <img src={assets.logo} alt={t("settings.brandKit.currentLogo", "Current primary logo")} style={{ maxWidth: 300, maxHeight: 96, objectFit: "contain" }} />
          </div>
        ) : null}
        <FileUpload
          files={[]}
          multiple={false}
          accept="image/png,image/jpeg,image/webp,image/svg+xml"
          disabled={!editable}
          label={t("settings.brandKit.choosePrimaryLogo", "Choose primary logo")}
          hint={t("settings.brandKit.logoPublishHint", "The file is published only when you select Publish.")}
          onFilesAdded={(files) => { if (files[0]) uploadImage("logo", files[0], "Logo"); }}
        />
        {assets.logo && editable ? <Btn variant="secondary" size="sm" onClick={() => setAsset("logo", null)}>{t("settings.brandKit.removeLogo", "Remove logo")}</Btn> : null}
      </Section>
      <Banner tone="info" title={t("settings.brandKit.additionalAssetsSoon", "Additional logo assets — Coming soon")}>
        {t("settings.brandKit.additionalAssetsSoonBody", "Reverse logo, icon / logomark, favicon, app icons, social image and email header logo are disabled until each has storage, reload and delivery support.")}
      </Banner>
    </>
  );

  const colour = (
    <>
      <Banner tone="neutral" title={t("settings.brandKit.colourOwnership", "Brand colours only")}>
        {t("settings.brandKit.colourOwnershipBody", "Success, warning, danger, neutral surfaces and component geometry come from the jrni.ai Design System and cannot be overridden by a tenant.")}
      </Banner>
      <Section title={t("settings.branding.brandColours", "Brand colors")} description={t("settings.brandKit.brandColoursBody", "These three roles are stored once and inherited everywhere.")}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(270px, 1fr))", gap: T.space[5], alignItems: "start" }}>
          <ColorPicker label={t("settings.branding.primary", "Primary")} value={brand.primary} onChange={(value) => set({ primary: value })} disabled={!editable} contrastAgainst="#FFFFFF" />
          <ColorPicker label={t("settings.branding.accent", "Accent")} value={brand.accent} onChange={(value) => set({ accent: value })} disabled={!editable} contrastAgainst="#FFFFFF" />
          <ColorPicker label={t("settings.branding.coverHero", "Cover / hero")} value={brand.cover} onChange={(value) => set({ cover: value })} disabled={!editable} contrastAgainst="#FFFFFF" />
        </div>
      </Section>
    </>
  );

  const typography = (
    <Section title={t("settings.branding.typography", "Typography")} description={t("settings.brandKit.typographyBody", "Choose from the approved font catalogue. Custom uploads are Coming soon.")}>
      <Field label={t("settings.branding.headingFont", "Heading font")}>
        <Dropdown value={brand.headingFont} onChange={(value) => set({ headingFont: value })} disabled={!editable}
          options={GOOGLE_FONTS.map((font) => ({ value: font, label: font }))} />
      </Field>
      <Field label={t("settings.branding.bodyFont", "Body font")}>
        <Dropdown value={brand.bodyFont} onChange={(value) => set({ bodyFont: value })} disabled={!editable}
          options={GOOGLE_FONTS.map((font) => ({ value: font, label: font }))} />
      </Field>
      <div style={{ padding: T.space[5], borderRadius: T.radius.md, background: T.n6 }}>
        <div style={{ fontFamily: brand.headingFont, fontSize: 22, fontWeight: 700, color: T.n1 }}>{t("settings.branding.theQuickBrownFox", "The quick brown fox")}</div>
        <div style={{ fontFamily: brand.bodyFont, fontSize: 14, color: T.n2, marginTop: T.space[2] }}>{t("settings.branding.jumpsOverTheLazyDog0123456789", "jumps over the lazy dog — 0123456789")}</div>
      </div>
    </Section>
  );

  const communications = (
    <Section title={t("settings.branding.communications", "Communications")} description={t("settings.brandKit.communicationsBody", "The identity customers see in messages and public footers.")}>
      <Field label={t("settings.branding.legalName", "Legal name")} hint={legalNameSupported ? t("settings.brandKit.legalNameBody", "Registered entity shown in transactional email footers.") : t("settings.brandKit.legalNameUnavailable", "Coming soon in this environment.")}>
        <Input value={brand.legalName} onChange={(event) => set({ legalName: event.target.value })} disabled={!editable || !legalNameSupported} />
      </Field>
      <Field label={t("settings.branding.emailSenderName", "Email sender name")}>
        <Input value={brand.senderName} onChange={(event) => set({ senderName: event.target.value })} disabled={!editable} />
      </Field>
      <Field label={t("settings.brandKit.publicFooterText", "Public footer text")}>
        <Input value={brand.footer} onChange={(event) => set({ footer: event.target.value })} disabled={!editable} />
      </Field>
    </Section>
  );

  /* Google Wallet is Coming soon (2026-09-28): the flag persists, but nothing in
     production issues or delivers a Google Wallet pass, so the row is disabled
     with no onChange. Publish sends only changed fields, so an untouchable row
     can never put `googleWalletEnabled` into the payload. Apple Wallet has live
     consumers and stays editable. */
  const wallets = (
    <Section title={t("settings.brandKit.wallets", "Wallet passes")} description={t("settings.brandKit.walletsBody", "Availability is canonical branding configuration. Pass appearance derives from the Brand Kit; there are no separate wallet colours.")}>
      <ToggleRow title={t("settings.brandKit.appleWallet", "Apple Wallet")} description={t("settings.brandKit.appleWalletBody", "Allow supported tickets and confirmations to offer an Apple Wallet pass.")}
        checked={brand.appleWalletEnabled} onChange={(checked) => set({ appleWalletEnabled: checked })} disabled={!editable} />
      <ToggleRow title={t("settings.brandKit.googleWallet", "Google Wallet")} description={t("settings.brandKit.notIncludedInPublish", "Coming soon · not included in Publish")}
        checked={false} disabled />
    </Section>
  );

  const roadmap = (
    <>
      <Banner tone="info" title={t("settings.brandKit.comingSoon", "Coming soon")}>
        {t("settings.brandKit.comingSoonBody", "These capabilities are intentionally disabled. They will become editable only after their API, persistence, reload and downstream delivery are complete.")}
      </Banner>
      <Section title={t("settings.brandKit.disabledCapabilities", "Disabled capabilities")} count={COMING_SOON.length}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: T.space[2] }}>
          {COMING_SOON.map((label) => (
            <ToggleRow key={label} title={label} description={t("settings.brandKit.notIncludedInPublish", "Coming soon · not included in Publish")} checked={false} disabled />
          ))}
        </div>
      </Section>
    </>
  );

  const panels: Record<PaneId, ReactNode> = { overview, logo, colour, type: typography, communications, wallets, roadmap };
  const deviceWidth = resolveDeviceWidths()[device];

  return (
    <Screen rail="branding">
      <LiveRegion text={announcement} />
      <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: T.space[3], padding: "14px 24px", borderBottom: `1px solid ${T.border}`, background: T.white, flexWrap: "wrap" }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: T.n1 }}>{t("settings.branding.brandKit", "Brand Kit")}</h1>
            <div style={{ fontSize: 12.5, color: T.soft }}>{t("settings.brandKit.canonicalSubtitle", "The single visual identity for every customer experience")}</div>
          </div>
          <span style={{ flex: 1 }} />
          {draftCount ? <span style={{ fontSize: 12.5, color: T.orange1 }}>{draftCount} {t("settings.brandKit.unpublished", "unpublished")}</span> : null}
          {isAdmin() ? <Btn variant="ghost" size="sm" icon={<Icon.list size={13} />} onClick={() => setAuditOpen(true)}>{t("settings.branding.auditTrail", "Audit trail")}</Btn> : null}
          <Btn variant="secondary" onClick={() => setPreviewOpen(true)}>{t("settings.brandKit.panePreview", "Preview")}</Btn>
        </div>

        <DataSourceStrip screen="settings-branding" ops={["getBranding", "updateBranding", "uploadBrandLogo"]} reqId="REQ-076" />
        {readFailed ? <Banner tone="danger" title={t("settings.brandKit.kitCouldnTBeRead", "This tenant's Brand Kit couldn't be read.")}>{t("settings.brandKit.kitCouldnTBeReadBody", "The fields are locked until the canonical Branding service is available.")}</Banner> : null}

        {layout === "phone" ? (
          <div style={{ padding: T.space[4], borderBottom: `1px solid ${T.border}`, background: T.n6 }}>
            <Field label={t("settings.brandKit.section", "Section")}>
              <Dropdown value={pane} onChange={(value) => setPane(value as PaneId)} options={items.map((item) => ({ value: item.id, label: String(item.label) }))} />
            </Field>
          </div>
        ) : null}

        <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
          {layout !== "phone" ? (
            <EditorSectionNav items={items} value={pane} onChange={(id) => setPane(id as PaneId)}
              ariaLabel={t("settings.brandKit.brandKitSections", "Brand Kit sections")}
              style={{ padding: T.space[4], borderRight: `1px solid ${T.border}`, background: T.white }} />
          ) : null}
          <main role="tabpanel" style={{ flex: 1, minWidth: 0, overflow: "auto", padding: "20px 24px 0", display: "flex", flexDirection: "column", gap: T.space[4], background: T.n6 }}>
            {panels[pane]}
            {/* Publish bar — app-local. jrni-ui has no EditorActionBar export (importing it crashed the route with React #130, 2026-09-28). */}
            <div data-testid="branding-actions" style={{ position: "sticky", bottom: 0, marginTop: T.space[3], padding: "12px 0", display: "flex", alignItems: "center", gap: T.space[2], flexWrap: "wrap", borderTop: `1px solid ${T.border}`, background: T.n6 }}>
              {!canWrite || readFailed ? (
                <span style={{ fontSize: 12.5, color: T.soft }}>{t("settings.brandKit.readOnlyNote", "You can view this Brand Kit, but your role cannot publish changes.")}</span>
              ) : (
                <>
                  <Btn variant="ghost" size="sm" onClick={resetDefaults}>{t("settings.brandKit.resetToDefaults", "Reset to defaults")}</Btn>
                  <span style={{ flex: 1 }} />
                  <Btn variant="secondary" disabled={!dirty} onClick={discard} testid="branding-actions-discard">{t("settings.brandKit.discard", "Discard")}</Btn>
                  <Btn disabled={!dirty} onClick={save} testid="branding-actions-publish">{t("settings.brandKit.publish", "Publish")}</Btn>
                </>
              )}
            </div>
          </main>
        </div>
      </div>

      {previewOpen ? (
        <div role="dialog" aria-modal="true" aria-label={t("settings.brandKit.customerPreview", "Customer preview")} onClick={() => setPreviewOpen(false)}
          style={{ position: "fixed", inset: 0, zIndex: 400, background: "rgba(20,22,30,0.68)", display: "flex", flexDirection: "column", padding: "18px clamp(10px, 4vw, 48px)" }}>
          <div onClick={(event) => event.stopPropagation()} style={{ margin: "0 auto", width: "100%", maxWidth: 1180, flex: 1, minHeight: 0, display: "flex", flexDirection: "column", gap: T.space[3] }}>
            <div style={{ display: "flex", alignItems: "center", gap: T.space[3], flexWrap: "wrap" }}>
              <strong style={{ color: T.white }}>{t("settings.brandKit.customerPreview", "Customer preview")}</strong>
              <span style={{ flex: 1 }} />
              <DevicePreviewToggle value={device} onChange={setDevice} size="sm" ariaLabel={t("settings.brandKit.previewWidth", "Preview width")} />
              <Btn size="sm" onClick={() => setPreviewOpen(false)}>{t("common.actions.close", "Close")}</Btn>
            </div>
            <div role="tablist" style={{ display: "flex", flexWrap: "wrap", gap: T.space[2] }}>
              {PREVIEW_JOURNEYS.map((item) => <Btn key={item.id} size="sm" variant={item.id === journey ? undefined : "secondary"} onClick={() => setJourney(item.id)}>{t(item.key, item.label)}</Btn>)}
            </div>
            <div style={{ flex: 1, overflow: "auto", display: "flex", justifyContent: "center" }}>
              <div style={{ width: "100%", maxWidth: device === "desktop" ? "100%" : deviceWidth, alignSelf: "flex-start", background: T.white, borderRadius: T.radius.md, overflow: "hidden" }}>
                <BrandPreview brand={brand} assets={assets} journey={journey} tenantName={tenant?.name ?? null} compact={device !== "desktop"} />
              </div>
            </div>
          </div>
        </div>
      ) : null}
      {auditOpen ? <AuditTrailDrawer target={{ type: "branding", id: "branding", name: t("settings.branding.branding", "Branding") }} onClose={() => setAuditOpen(false)} /> : null}
    </Screen>
  );
}
